// Cache for storing fetched machine data
let machineCache = {
  data: [],
  timestamp: null,
  isLoading: false,
  pendingPromise: null,
};

// Cache expiration time in milliseconds (5 minutes)
const CACHE_EXPIRY = 5 * 60 * 1000;

// Global loading state handler
let loadingHandlers = [];

// Register a loading state handler
export const registerMachineLoadingHandler = (handler) => {
  loadingHandlers.push(handler);
  return () => {
    loadingHandlers = loadingHandlers.filter((h) => h !== handler);
  };
};

// Update all registered handlers with the current loading state
const updateLoadingState = (isLoading, message = null) => {
  loadingHandlers.forEach((handler) => handler(isLoading, message));
};

// Utility function to fetch machines from Zoho Creator
export const fetchMachines = async (forceRefresh = false) => {
  // Return cached data if available and not expired and not forced to refresh
  const now = Date.now();
  if (
    !forceRefresh &&
    machineCache.data.length > 0 &&
    machineCache.timestamp &&
    now - machineCache.timestamp < CACHE_EXPIRY
  ) {
    console.log("Returning cached machine data");
    return machineCache.data;
  }

  // If a request is already in progress, return the pending promise
  if (machineCache.isLoading && machineCache.pendingPromise) {
    console.log(
      "Machine request already in progress, waiting for it to complete"
    );
    return machineCache.pendingPromise;
  }

  const conn_name = "crmwidgetconnection";
  const apiUrl =
    "https://www.zohoapis.com/creator/v2.1/data/sst1source/source-erp/report/All_Machines";

  const req_data = {
    parameters: {},
    headers: {
      "Content-Type": "application/json",
    },
    method: "GET",
    url: apiUrl,
    param_type: 1,
  };

  // Set loading state
  machineCache.isLoading = true;
  updateLoadingState(true, "Loading machine data...");

  // Create and store the promise
  machineCache.pendingPromise = new Promise(async (resolve, reject) => {
    try {
      console.log("Fetching fresh machine data from API...");
      const response = await ZOHO.CRM.CONNECTION.invoke(conn_name, req_data);

      console.log("Machine API Response:", response);

      if (
        !response ||
        response.code !== "SUCCESS" ||
        !response.details ||
        !response.details.statusMessage ||
        !response.details.statusMessage.data
      ) {
        console.error("Invalid machine response structure:", response);
        // Return cached data if available when API fails
        if (machineCache.data.length > 0) {
          resolve(machineCache.data);
        } else {
          resolve([]);
        }
        return;
      }

      const machines = response.details.statusMessage.data.map((machine) => ({
        id: machine.ID,
        name: machine.Name || "Unnamed Machine",
        machineType: machine.Machine_Type || "",
        // Include any other fields you might need
        ...machine,
      }));

      // Update cache
      machineCache.data = machines;
      machineCache.timestamp = Date.now();

      // Add a small delay to show the loader even for quick responses
      setTimeout(() => {
        updateLoadingState(false);
        resolve(machines);
      }, 500);
    } catch (error) {
      console.error("Error fetching machines:", error);
      // Return cached data if available when API fails
      if (machineCache.data.length > 0) {
        resolve(machineCache.data);
      } else {
        resolve([]);
      }
    } finally {
      // Clear loading state (after timeout)
      setTimeout(() => {
        machineCache.isLoading = false;
        machineCache.pendingPromise = null;
        updateLoadingState(false);
      }, 500);
    }
  });

  return machineCache.pendingPromise;
};

// Function to filter machines by search term
export const filterMachinesBySearchTerm = (machines, searchTerm) => {
  if (
    !searchTerm ||
    typeof searchTerm !== "string" ||
    !machines ||
    machines.length === 0
  ) {
    return machines;
  }

  const normalizedSearchTerm = searchTerm.toLowerCase().trim();

  // First prioritize machines that start with the search term
  const startsWithMatches = machines.filter(
    (machine) =>
      machine.name &&
      machine.name.toLowerCase().startsWith(normalizedSearchTerm)
  );

  // Then include machines that contain the search term elsewhere
  const containsMatches = machines.filter(
    (machine) =>
      machine.name &&
      machine.name.toLowerCase().includes(normalizedSearchTerm) &&
      !(
        machine.name &&
        machine.name.toLowerCase().startsWith(normalizedSearchTerm)
      )
  );

  // Combine both results: first the "starts with" matches, then the "contains" matches
  return [...startsWithMatches, ...containsMatches];
};

// Function to invalidate cache (use after updates)
export const invalidateMachineCache = () => {
  console.log("Invalidating machine cache");
  machineCache.timestamp = null;
};
